import type { ApiFieldError, ApiResponse, PageMeta } from '@mm/shared';

/** Error returned by the API envelope (or a network failure). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly errors: ApiFieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface SessionPayload {
  accessToken: string;
  expiresIn: number;
  user: CurrentUser;
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'ACCOUNTANT' | 'VIEWER';
  permissions: string[];
}

/** The session exists but is locked (inactivity): the PIN or a passkey unlocks it. */
export interface LockedUser {
  name: string;
  email: string;
  hasPin: boolean;
  hasPasskey: boolean;
}
export interface LockedPayload {
  locked: true;
  user: LockedUser;
}
export type RefreshResult = SessionPayload | LockedPayload | null;
export const isLockedResult = (r: RefreshResult): r is LockedPayload => !!r && 'locked' in r;

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

// The access token lives only in memory; the refresh token is an httpOnly cookie.
let accessToken: string | null = null;
let refreshInFlight: Promise<RefreshResult> | null = null;
let onSessionExpired: (() => void) | null = null;
let onSessionLocked: ((user: LockedUser) => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function setSessionExpiredHandler(handler: (() => void) | null) {
  onSessionExpired = handler;
}

export function setSessionLockedHandler(handler: ((user: LockedUser) => void) | null) {
  onSessionLocked = handler;
}

/** After a 401: refresh; a locked session goes to the lock screen instead of signing out. */
async function recoverSession(): Promise<boolean> {
  const session = await refreshSession();
  if (isLockedResult(session)) {
    onSessionLocked?.(session.user);
    throw new ApiError(423, 'The app is locked. Unlock it to continue.');
  }
  if (session) return true;
  onSessionExpired?.();
  return false;
}

export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

async function parse<T>(res: Response): Promise<ApiResponse<T>> {
  try {
    return (await res.json()) as ApiResponse<T>;
  } catch {
    return { success: false, message: `Unexpected response (${res.status})`, errors: [] };
  }
}

/** Exchange the refresh cookie for a new access token. Concurrent callers share one request. */
export function refreshSession(): Promise<RefreshResult> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' });
      const body = await parse<SessionPayload | LockedPayload>(res);
      if (!res.ok || !body.success) {
        accessToken = null;
        return null;
      }
      if ('locked' in body.data) {
        accessToken = null;
        return body.data;
      }
      accessToken = body.data.accessToken;
      return body.data;
    } catch {
      return null;
    } finally {
      setTimeout(() => (refreshInFlight = null), 0);
    }
  })();
  return refreshInFlight;
}

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; query?: QueryParams } = {},
  retried = false,
): Promise<{ data: T; meta?: PageMeta }> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let res: Response;
  try {
    res = await fetch(`/api${path}${buildQuery(options.query)}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'Cannot reach the server. Check your connection and try again.');
  }

  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    if (await recoverSession()) return request<T>(method, path, options, true);
  }

  const body = await parse<T>(res);
  if (!res.ok || !body.success) {
    const failure = body.success ? null : body;
    throw new ApiError(
      res.status,
      failure?.message ?? `Request failed (${res.status})`,
      failure?.errors ?? [],
    );
  }
  return { data: body.data, meta: body.meta };
}

export const api = {
  get: async <T>(path: string, query?: QueryParams) =>
    (await request<T>('GET', path, { query })).data,
  list: <T>(path: string, query?: QueryParams) => request<T[]>('GET', path, { query }),
  post: async <T>(path: string, body?: unknown) =>
    (await request<T>('POST', path, { body: body ?? {} })).data,
  put: async <T>(path: string, body: unknown) => (await request<T>('PUT', path, { body })).data,
  delete: async <T>(path: string, body?: unknown) =>
    (await request<T>('DELETE', path, { body })).data,
};

/** Authenticated binary request (downloads, uploads); retries once after a token refresh. */
async function rawRequest(
  method: string,
  path: string,
  init: { body?: BodyInit; query?: QueryParams } = {},
  retried = false,
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  let res: Response;
  try {
    res = await fetch(`/api${path}${buildQuery(init.query)}`, {
      method,
      headers,
      body: init.body,
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(0, 'Cannot reach the server. Check your connection and try again.');
  }
  if (res.status === 401 && !retried) {
    if (await recoverSession()) return rawRequest(method, path, init, true);
  }
  if (!res.ok) {
    const body = await parse<unknown>(res);
    const failure = body.success ? null : body;
    throw new ApiError(
      res.status,
      failure?.message ?? `Request failed (${res.status})`,
      failure?.errors ?? [],
    );
  }
  return res;
}

/** File name from Content-Disposition (RFC 5987 first). */
function fileNameFrom(res: Response, fallback: string) {
  const cd = res.headers.get('Content-Disposition') ?? '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) return decodeURIComponent(star[1]!);
  const plain = /filename="([^"]+)"/i.exec(cd);
  return plain?.[1] ?? fallback;
}

/** Download a file from the API and save it in the browser. */
export async function downloadFile(path: string, query?: QueryParams, fallbackName = 'download') {
  const res = await rawRequest('GET', path, { query });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileNameFrom(res, fallbackName);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** multipart/form-data upload; returns the envelope's data. */
export async function uploadFile<T>(path: string, form: FormData): Promise<T> {
  const res = await rawRequest('POST', path, { body: form });
  const body = await parse<T>(res);
  if (!body.success) throw new ApiError(res.status, body.message, body.errors);
  return body.data;
}
